import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, AlertTitle, InputAdornment, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import colors from 'common/constants/colors';

import {
  MANUAL_PRODUCT_ID_CODE_LENGTH,
  MANUAL_PRODUCT_ID_FORMAT_HINT,
  PRODUCT_ID_PREFIX,
  buildManualProductId,
  isManualProductIdCodeValid,
  normalizeManualProductIdCode,
} from '../utils/manualProductId';

// POM-6514 — why the Product ID field has appeared and how a Product ID is put together. Shown in
// the dialog rather than as a snackbar so it stays beside the field it explains.
//
// While the typed Product ID cannot be submitted (conflictText: already in the catalog, or used by
// another variant) the banner says so as an error instead, and returns to the warning as soon as a
// different one is typed. Warning colours are the app's inline-warning ones -- the theme's warning
// palette is navy; the error ones are the theme's.
export const ManualProductIdInstructions = ({ conflictText }) => {
  const isConflict = Boolean(conflictText);

  return (
    <Alert
      severity={isConflict ? 'error' : 'warning'}
      icon={
        isConflict ? (
          <ErrorOutlineIcon sx={{ fontSize: 28 }} />
        ) : (
          <WarningAmberIcon sx={{ fontSize: 28, color: colors.warningAlertIcon }} />
        )
      }
      sx={(theme) => {
        const accent = isConflict ? theme.palette.error.main : colors.warningAlertIcon;

        return {
          ...(!isConflict && {
            bgcolor: colors.warningAlertSurface,
            color: colors.warningAlertText,
          }),
          border: `1px solid ${accent}`,
          borderLeft: `6px solid ${accent}`,
          borderRadius: 2,
          py: 1.5,
        };
      }}
    >
      <AlertTitle sx={{ fontWeight: 700, fontSize: 14 }}>
        {isConflict ? 'Product ID already in use' : 'Enter the Product ID manually'}
      </AlertTitle>
      <Typography variant='body2' sx={{ mb: 1 }}>
        {conflictText ||
          "We can't generate the Product ID for the provided information. You can enter the Product ID manually using the following information."}
      </Typography>
      <Typography variant='body2' component='div'>
        Format: <strong>{PRODUCT_ID_PREFIX}</strong> + drug code (3 characters, e.g. FLX) + strength
        code (4 digits, e.g. 40 mg → 0400, 0.5 mg → 0005, 2% → 0200) + form code (set by the
        selected Form, e.g. TB for Tablet). Example: PRXFLX0400TB.
      </Typography>
      <Typography variant='body2'>
        {PRODUCT_ID_PREFIX} and the form code are added for you. The Product ID must be unique.
      </Typography>
    </Alert>
  );
};

ManualProductIdInstructions.propTypes = {
  conflictText: PropTypes.string,
};

ManualProductIdInstructions.defaultProps = {
  conflictText: '',
};

// The editable middle of a Product ID -- drug code + strength code -- between the locked PRX
// prefix and the selected form's code. conflictText marks a complete one that still cannot be
// submitted; the banner above names the reason, so the field only asks for a unique one. Both
// clear once the admin types a different one.
const ManualProductIdField = ({
  id,
  value,
  onChange,
  shortCode,
  formSelected,
  drugCode,
  size,
  autoFocus,
  conflictText,
}) => {
  const isInvalid = Boolean(value) && !isManualProductIdCodeValid(value);

  const getHelperText = () => {
    if (!formSelected) return 'Select a Form first';
    if (isInvalid) return MANUAL_PRODUCT_ID_FORMAT_HINT;
    if (conflictText) return 'Enter a unique Product ID';
    if (value) return `Product ID: ${buildManualProductId(value, shortCode)}`;
    return MANUAL_PRODUCT_ID_FORMAT_HINT;
  };

  const placeholder = `${drugCode}0400`;
  // The input is only as wide as its text, so the form code sits right after what was typed and
  // the three parts read as one Product ID. Monospace keeps `ch` an exact character width.
  const inputWidth = `${Math.max((value || placeholder).length, 1) + 0.5}ch`;

  return (
    <TextField
      id={id}
      label='Product ID'
      required
      size={size}
      fullWidth
      autoFocus={autoFocus}
      value={value}
      disabled={!formSelected}
      placeholder={placeholder}
      onChange={(event) => onChange(normalizeManualProductIdCode(event.target.value))}
      error={isInvalid || Boolean(conflictText)}
      helperText={getHelperText()}
      InputProps={{
        inputProps: {
          maxLength: MANUAL_PRODUCT_ID_CODE_LENGTH,
          style: { textTransform: 'uppercase', width: inputWidth },
        },
        sx: {
          fontFamily: 'monospace',
          '& .MuiInputAdornment-root': { m: 0 },
          '& .MuiInputAdornment-root .MuiTypography-root': { fontFamily: 'monospace' },
        },
        startAdornment: <InputAdornment position='start'>{PRODUCT_ID_PREFIX}</InputAdornment>,
        endAdornment: shortCode ? (
          <InputAdornment position='end'>{shortCode}</InputAdornment>
        ) : null,
      }}
    />
  );
};

ManualProductIdField.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  shortCode: PropTypes.string,
  formSelected: PropTypes.bool.isRequired,
  drugCode: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  autoFocus: PropTypes.bool,
  conflictText: PropTypes.string,
};

ManualProductIdField.defaultProps = {
  shortCode: '',
  drugCode: '',
  size: 'medium',
  autoFocus: false,
  conflictText: '',
};

export default ManualProductIdField;
